import React from 'react'

import styles from './serviceMedia.module.css'
import { LuTrash2, LuUpload } from 'react-icons/lu'
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = "image/jpeg,image/png,image/gif,image/webp";

function ServiceMedia({
    onAddMedia=() => {},
    onUploadImage=async () => {},
    onDeleteImage=async () => {},
    images,
}) {
    const fileInputRef = React.useRef(null);
    const [pendingUpload, setPendingUpload] = React.useState(null);
    const [isUploading, setIsUploading] = React.useState(false);
    const [uploadError, setUploadError] = React.useState("");
    const [selectedImageId, setSelectedImageId] = React.useState(null);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState("");

    const galleryImages = React.useMemo(() => Array.isArray(images) ? images : [], [images]);

    React.useEffect(() => {
        if (!pendingUpload?.previewUrl) {
            return undefined;
        }

        return () => URL.revokeObjectURL(pendingUpload.previewUrl);
    }, [pendingUpload]);

    // Default to the first image, and fall back to it whenever the selection disappears (e.g. after a delete).
    React.useEffect(() => {
        if (galleryImages.length === 0) {
            setSelectedImageId(null);
            return;
        }

        const stillExists = galleryImages.some((image) => image.id === selectedImageId);
        if (!stillExists) {
            setSelectedImageId(galleryImages[0].id);
        }
    }, [galleryImages, selectedImageId]);

    const selectedImage = galleryImages.find((image) => image.id === selectedImageId) || galleryImages[0] || null;

    const handleAddMediaClick = () => {
        setUploadError("");
        fileInputRef.current?.click();
        onAddMedia();
    };

    const handleSelectFile = (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";

        if (!file) {
            return;
        }

        if (file.size > MAX_IMAGE_BYTES) {
            setUploadError("Image must be 10 MB or smaller.");
            return;
        }

        setUploadError("");
        setPendingUpload({ file, previewUrl: URL.createObjectURL(file) });
    };

    const handleCancelUpload = () => {
        setPendingUpload(null);
        setUploadError("");
    };

    const handleConfirmUpload = async () => {
        if (!pendingUpload || isUploading) {
            return;
        }

        setIsUploading(true);
        setUploadError("");

        try {
            await onUploadImage(pendingUpload.file);
            setPendingUpload(null);
        } catch (error) {
            setUploadError(error?.message || "Failed to upload image.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteClick = async () => {
        if (!selectedImage || isDeleting) {
            return;
        }

        const confirmed = window.confirm("Delete this image? This cannot be undone.");
        if (!confirmed) {
            return;
        }

        setIsDeleting(true);
        setDeleteError("");

        try {
            await onDeleteImage(selectedImage.id);
        } catch (error) {
            setDeleteError(error?.message || "Failed to delete image.");
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <section className={styles.container} aria-label="Service images">
            <input
                ref={fileInputRef}
                type="file"
                aria-label="Upload service image"
                accept={ACCEPTED_IMAGE_TYPES}
                className={styles.hiddenFileInput}
                onChange={handleSelectFile}
            />
            {/* <div className={styles.header}>
                <h2>Media</h2>
                <button type="button" className={styles.addMediaButton} onClick={handleAddMediaClick} disabled={isUploading || isDeleting}>
                    Add images
                </button>
            </div> */}
            <div className={styles.galleryContainer}>
                {galleryImages.length > 0 && (
                    <div className={styles.gallery}>
                        <button 
                            type="button"
                            className={styles.addMediaButton} 
                            onClick={handleAddMediaClick} 
                            disabled={isUploading || isDeleting}
                        >
                            Upload Media
                        </button>
                        {galleryImages.map((image) => (
                            <button
                                type="button"
                                key={image.id ?? image.url}
                                className={`${styles.galleryImage}${image.id === selectedImageId ? ` ${styles.galleryImageSelected}` : ""}`}
                                onClick={() => setSelectedImageId(image.id)}
                                aria-label={image.alt_text || "Select service image"}
                                aria-pressed={image.id === selectedImageId}
                            >
                                <img src={image.url} alt={image.alt_text || "Service image"} />
                            </button>
                        ))}
                    </div>
                )}
                <div className={styles.mainImage} style={galleryImages.length === 0 ? { gridColumn: "1 / -1" } : {}}>
                    {pendingUpload ? (
                        <img src={pendingUpload.previewUrl} alt="Selected upload preview" className={styles.mainImageMedia} />
                    ) : selectedImage ? (
                        <img src={selectedImage.url} alt={selectedImage.alt_text || "Main image"} className={styles.mainImageMedia} />
                    ) : (
                        <button type="button" className={styles.mainImagePlaceholder} onClick={handleAddMediaClick}>
                            <span>Add service images</span>
                            <small>JPG, PNG, GIF or WebP, up to 10 MB</small>
                        </button>
                    )}
                    {pendingUpload ? (
                        <div className={styles.uploadActions}>
                            <button
                                type="button"
                                className={styles.cancelUploadButton}
                                onClick={handleCancelUpload}
                                disabled={isUploading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.confirmUploadButton}
                                onClick={handleConfirmUpload}
                                disabled={isUploading}
                            >
                                {isUploading ? "Uploading..." : "Upload"}
                                <LuUpload />
                            </button>
                        </div>
                    ) : selectedImage && (
                        <div className={styles.uploadActions}>
                            <button
                                type="button"
                                className={styles.deleteImageButton}
                                onClick={handleDeleteClick}
                                disabled={isDeleting}
                            >
                                {isDeleting ? "Deleting..." : "Delete"}
                                <LuTrash2 />
                            </button>
                        </div>
                    )}
                </div>
            </div>
            {uploadError && <p role="alert" className={styles.uploadError}>{uploadError}</p>}
            {deleteError && <p role="alert" className={styles.uploadError}>{deleteError}</p>}
        </section>
    )
}

export default ServiceMedia
